import { useEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react'

/** Quanto tempo o dedo fica parado antes de virar "segurar". */
export const SEGURAR_MS = 500
/** O quanto ele pode tremer sem deixar de ser um "segurar". */
const TREMOR = 10

/**
 * Segurar o dedo (ou o mouse) sobre alguma coisa.
 *
 * ## Ele saiu do `ItemRow` ANTES da segunda cópia
 *
 * O gesto nasceu nos lembretes (decisão 220) e ia nascer de novo na lista de
 * transações. As três armadilhas que ele resolve foram pagas uma vez, no
 * navegador, e nenhuma delas é óbvia o bastante para sobreviver a uma segunda
 * escrita do zero:
 *
 * 1. **O `click` fantasma.** Um long-press termina num `click` que o navegador
 *    dispara de qualquer jeito. Sem a trava na fase de **captura**, segurar
 *    sobre um link abriria o menu **e** navegaria.
 * 2. **O tremor.** Dedo não fica parado; sem folga, quase nenhum "segurar" se
 *    completa. Com folga demais, um deslize vira segurar.
 * 3. **A limpeza.** O timer tem de morrer quando o componente sai, senão ele
 *    dispara sobre uma tela que não existe mais.
 *
 * ## O que ele NÃO faz
 *
 * Deslize, arraste e o menu que abre continuam de quem chama — são gestos
 * diferentes com regras diferentes. Por isso `aoMover` devolve o deslocamento:
 * quem quiser um deslize por cima usa o mesmo evento, sem duplicar a conta.
 *
 * ## E ele NÃO é a única porta
 *
 * *"Long-press é gesto de ponteiro"* (decisão 220d): teclado e leitor de tela
 * não têm como executá-lo. Quem usa este gancho **precisa** oferecer um botão
 * de verdade para a mesma ação — nos lembretes é a alça, na lista de transações
 * é o ícone do instrumento.
 */
export function useSegurar(aoSegurar: () => void): {
  /** No `onPointerDown` do alvo. */
  aoApertar: (e: ReactPointerEvent) => void
  /** No `onPointerMove`. Devolve o deslocamento desde o toque, para quem quiser
   *  montar outro gesto por cima — `null` quando não há gesto em curso. */
  aoMover: (e: ReactPointerEvent) => { dx: number; dy: number } | null
  /** No `onPointerUp` e no `onPointerCancel`. */
  encerrar: () => void
  /** Marca que o clique seguinte é fantasma — para quem completa outro gesto. */
  suprimirProximoClique: () => void
  /** No `onClickCapture`. */
  aoClicarNaCaptura: (e: ReactMouseEvent) => void
} {
  const gesto = useRef<{ x: number; y: number; timer: number } | null>(null)
  const suprimir = useRef(false)

  useEffect(() => {
    return () => {
      if (gesto.current !== null) window.clearTimeout(gesto.current.timer)
    }
  }, [])

  function encerrar() {
    if (gesto.current !== null) {
      window.clearTimeout(gesto.current.timer)
      gesto.current = null
    }
  }

  return {
    // `aoSegurar` entra pela closure deste render, e não por um ref: guardá-lo
    // num ref exigiria escrevê-lo durante o render, que é o que
    // `react-hooks/refs` proíbe. A closure é a do momento do toque, que é a
    // certa — meio segundo depois ela continua apontando para a mesma linha.
    aoApertar: (e) => {
      const timer = window.setTimeout(() => {
        suprimir.current = true
        encerrar()
        aoSegurar()
      }, SEGURAR_MS)
      gesto.current = { x: e.clientX, y: e.clientY, timer }
    },

    aoMover: (e) => {
      const g = gesto.current
      if (g === null) return null
      const dx = e.clientX - g.x
      const dy = e.clientY - g.y
      // Saiu do tremor: não é mais "segurar". O gesto continua vivo porque quem
      // chama pode estar montando um deslize com o mesmo movimento.
      if (Math.abs(dx) > TREMOR || Math.abs(dy) > TREMOR) window.clearTimeout(g.timer)
      return { dx, dy }
    },

    encerrar,
    suprimirProximoClique: () => {
      suprimir.current = true
    },
    aoClicarNaCaptura: (e) => {
      if (!suprimir.current) return
      e.preventDefault()
      e.stopPropagation()
      suprimir.current = false
    },
  }
}
